import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    error,
    accountStatus,
  } = useAuth()

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    await login(email, password)
    setSubmitting(false)
  }

  const handleGoogleLogin = async () => {
    setSubmitting(true)
    await loginWithGoogle()
    setSubmitting(false)
  }

  const isLoading = loading || submitting

  // Firebase session exists but the portal rejected it — explain and offer sign-out
  if (accountStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-2xl mb-4">Portal</h1>
          <p className="text-sm text-[var(--color-text-secondary)] mb-6">
            {accountStatus === 'not_registered'
              ? "Your account isn't set up yet. Ask your administrator for an invitation."
              : 'Your account has been deactivated. Contact your administrator.'}
          </p>
          <button onClick={() => logout()} className="btn btn-secondary w-full">
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl mb-8 text-center">Portal</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? 'Loading...' : 'Log In'}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 border-t border-[var(--color-border)]" />
          <span className="text-sm text-[var(--color-text-tertiary)]">or</span>
          <div className="flex-1 border-t border-[var(--color-border)]" />
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="btn btn-secondary w-full"
        >
          {isLoading ? 'Loading...' : 'Continue with Google'}
        </button>

        <p className="mt-6 text-center text-sm text-[var(--color-text-secondary)]">
          Access is by invitation. Ask your administrator if you need an account.
        </p>
      </div>
    </div>
  )
}
